import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useTeamData } from '../../../app/use-team-data'
import type { InspectionDraft } from '../../../offline/data-db'
import { DRAFTS_QUERY_KEY } from '../../../offline/query-keys'

/**
 * Les contrôles dont la fiche est **verrouillée** sur cette tablette : transmise, et pas rouverte.
 *
 * C'est ce que lit `hasQueuedHandOver` pour un envoi refusé : « Rouvrir la fiche »
 * (`retrySubmission`) déverrouille le brouillon mais laisse l'entrée refusée en tête de file, et
 * l'état de file seul faisait dire « Lecture seule » à la pré-fiche d'une fiche modifiable.
 */
export function useLockedInquiryIds(): ReadonlySet<string> {
  return useLockedDrafts().ids
}

export interface LockedDrafts {
  ids: ReadonlySet<string>
  /**
   * Quand les brouillons ont été lus, `null` tant que la lecture n'est pas finie. ⚠️ **Finie, pas
   * réussie** : une lecture refusée vaut `0`, sans verrou connu. Attendre son succès laisserait
   * vide, pour toujours, ce qui attend les verrous — l'agenda d'une journée pleine (M21).
   */
  readAt: number | null
}

/**
 * {@link useLockedInquiryIds}, avec l'instant de sa lecture : de quoi refaire une lecture qui en
 * dépend dès qu'un verrou bouge, et ne pas la faire tant que les verrous ne sont pas connus.
 */
export function useLockedDrafts(): LockedDrafts {
  const data = useTeamData()
  const drafts = useQuery({
    queryKey: DRAFTS_QUERY_KEY,
    queryFn: () => data.drafts.toArray(),
  })

  const ids = useMemo(
    () =>
      new Set(
        (drafts.data ?? [])
          .filter((draft) => draft.lockedAt !== null)
          .map((draft) => draft.inquiryId),
      ),
    [drafts.data],
  )

  return { ids, readAt: drafts.isPending ? null : drafts.dataUpdatedAt }
}

/**
 * La fiche que l'éditeur doit peindre quand la base lui **relit** celle qu'il a ouverte.
 *
 * L'état d'édition part du brouillon relu à l'ouverture et n'y revient pas : c'est ce qui rend la
 * frappe instantanée. Une seule chose de la base l'emporte sur lui, **un verrou posé ailleurs** :
 * la PWA ouverte deux fois partage une seule base, l'autre onglet transmet, et celui-ci doit passer
 * en consultation de la fiche **telle qu'elle est partie** — pas continuer une saisie que
 * `updateDraft` refuse désormais (`LockedDraftError`) et qui ne partirait jamais.
 *
 * ⚠️ **Un verrou se suit, un déverrouillage non.** Une relecture partie juste avant la
 * transmission de cet onglet peut revenir après elle, fiche encore ouverte : la suivre rendrait
 * modifiable à l'écran une fiche verrouillée en base. Rester en consultation ne coûte rien ;
 * rouvrir la fiche la relit.
 */
export function followStoredLock(
  current: InspectionDraft,
  stored: InspectionDraft,
): InspectionDraft {
  if (stored.lockedAt === null || stored.lockedAt === current.lockedAt) return current

  return stored
}
